import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../lib/api.js";
import { PageLayout } from "../components/PageLayout.js";
import { ErrorState, LoadingState } from "../components/States.js";
import { useToast } from "../components/Toast.js";
import { PasswordInput } from "../components/PasswordInput.js";

type Me = {
  user: {
    id: string;
    email: string;
    display_name: string | null;
    role: string;
    is_enabled: boolean;
  };
};

function emailLooksValid(email: string) {
  return /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(email.trim());
}

export function AdminAccount() {
  const nav = useNavigate();
  const toast = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [me, setMe] = useState<Me["user"] | null>(null);
  const [email, setEmail] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [currentPasswordForEmail, setCurrentPasswordForEmail] = useState("");

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmNewPassword, setConfirmNewPassword] = useState("");

  async function refresh() {
    setLoading(true);
    setError(null);
    try {
      const data = await apiFetch<Me>("/api/auth/me");
      setMe(data.user);
      setEmail(data.user.email);
      setDisplayName(data.user.display_name ?? "");
    } catch {
      nav("/login");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailChanged = me != null && email.trim().toLowerCase() != me.email;
  const emailValid = emailLooksValid(email);

  const profileDirty = useMemo(() => {
    if (!me) return false;
    return displayName.trim() !== (me.display_name ?? "") || email.trim().toLowerCase() !== me.email;
  }, [me, displayName, email]);

  async function saveProfile() {
    if (!me) return;
    const nextEmail = email.trim().toLowerCase();
    const nextName = displayName.trim() === "" ? null : displayName.trim();
    if (!emailLooksValid(nextEmail)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (emailChanged && currentPasswordForEmail.trim() === "") {
      setError("Current password is required to change your email.");
      return;
    }

    setSaving(true);
    setError(null);
    try {
      const data = await apiFetch<Me>("/api/auth/me", {
        method: "PATCH",
        body: {
          email: nextEmail,
          display_name: nextName,
          current_password: emailChanged ? currentPasswordForEmail : undefined
        }
      });
      setMe(data.user);
      setEmail(data.user.email);
      setDisplayName(data.user.display_name ?? "");
      setCurrentPasswordForEmail("");
      toast.push({ kind: "success", message: "Account updated." });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  const passwordError =
    newPassword !== "" && newPassword.length < 8
      ? "New password must be at least 8 characters."
      : confirmNewPassword !== "" && confirmNewPassword !== newPassword
        ? "New passwords do not match."
        : null;

  async function changePassword() {
    if (passwordError) return;
    setSaving(true);
    setError(null);
    try {
      await apiFetch<{ ok: true }>("/api/auth/change-password", {
        method: "POST",
        body: { current_password: currentPassword, new_password: newPassword }
      });
      setCurrentPassword("");
      setNewPassword("");
      setConfirmNewPassword("");
      toast.push({ kind: "success", message: "Password updated." });
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <PageLayout title="Account">
      {error && <ErrorState detail={error} />}
      {loading && <LoadingState />}

      {!loading && me && (
        <div style={{ display: "grid", gap: 12, marginTop: 12 }}>
          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Profile</div>
            <div className="help" style={{ marginBottom: 12 }}>
              Your email is used to sign in. If you change it, you’ll use the new email on your next login.
            </div>

            <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
              <label className="field">
                <span className="label">User ID</span>
                <input className="input mono" value={me.id} readOnly />
              </label>

              <label className="field">
                <span className="label">Display name</span>
                <input className="input" value={displayName} onChange={(e) => setDisplayName(e.target.value)} />
              </label>

              <label className="field">
                <span className="label">Email</span>
                <input className="input" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                {!emailValid ? (
                  <div className="help" style={{ color: "var(--danger-text)" }}>
                    Enter a valid email address.
                  </div>
                ) : null}
              </label>

              {emailChanged ? (
                <label className="field">
                  <span className="label">Current password (required to change email)</span>
                  <PasswordInput
                    value={currentPasswordForEmail}
                    onChange={setCurrentPasswordForEmail}
                    autoComplete="current-password"
                    aria-label="Current password for email change"
                  />
                </label>
              ) : null}
            </div>

            <div className="actions">
              <button
                className="btn"
                type="button"
                disabled={saving}
                onClick={() => {
                  setEmail(me.email);
                  setDisplayName(me.display_name ?? "");
                  setCurrentPasswordForEmail("");
                }}
              >
                Reset
              </button>
              <button
                className="btn btnPrimary"
                type="button"
                disabled={saving || !profileDirty || !emailValid || (emailChanged && currentPasswordForEmail.trim() === "")}
                onClick={() => void saveProfile()}
              >
                Save changes
              </button>
            </div>
          </div>

          <div className="card">
            <div style={{ fontWeight: 800, marginBottom: 6 }}>Change password</div>
            <div className="help" style={{ marginBottom: 12 }}>
              Choose a strong password. Minimum 8 characters.
            </div>

            <div style={{ display: "grid", gap: 10, marginBottom: 12 }}>
              <label className="field">
                <span className="label">Current password</span>
                <PasswordInput value={currentPassword} onChange={setCurrentPassword} autoComplete="current-password" aria-label="Current password" />
              </label>
              <label className="field">
                <span className="label">New password</span>
                <PasswordInput value={newPassword} onChange={setNewPassword} autoComplete="new-password" aria-label="New password" />
              </label>
              <label className="field">
                <span className="label">Confirm new password</span>
                <PasswordInput value={confirmNewPassword} onChange={setConfirmNewPassword} autoComplete="new-password" aria-label="Confirm new password" />
                {passwordError ? (
                  <div className="help" style={{ color: "var(--danger-text)" }}>
                    {passwordError}
                  </div>
                ) : null}
              </label>
            </div>

            <div className="actions">
              <button
                className="btn btnPrimary"
                type="button"
                disabled={saving || !currentPassword || !newPassword || !confirmNewPassword || passwordError != null}
                onClick={() => void changePassword()}
              >
                Update password
              </button>
            </div>
          </div>
        </div>
      )}
    </PageLayout>
  );
}
